import { z } from "zod";
import { Accession, Cik, IsoDate, Timestamp } from "./primitives.js";
/**
 * Forms that can DISCOVER a separation — a filing of one of these types can open a
 * situation Mosaic did not previously know about.
 */
export const DISCOVERY_FORM_TYPES = [
    "10-12B", "10-12B/A", "10-12G", "10-12G/A",
    "8-A12B", "8-A12B/A", "8-A12G",
    "S-1", "S-1/A", "S-4", "S-4/A",
    "8-K", "8-K/A",
    "DEF 14A", "PRE 14A", "DEFA14A", "DEFM14A", "PREM14A",
    "424B3", "424B4", "425",
];
/**
 * Forms that MONITOR a separation already known about, and cannot discover one: none of
 * them says anything about a separation on its own, so they matter only for companies
 * already in a situation.
 */
export const MONITORING_FORM_TYPES = [
    "3", "4", "5", "3/A", "4/A", "5/A",
    "SC 13D", "SC 13D/A", "SC 13G", "SC 13G/A",
    "10-K", "10-K/A", "10-Q", "10-Q/A",
    "15-12B", "25", "25-NSE",
];
/**
 * Section 16 ownership forms, which are XML rather than prose and are read as data, not
 * through the text extractor.
 */
export const OWNERSHIP_FORM_TYPES = ["3", "4", "5", "3/A", "4/A", "5/A"];
/**
 * Whether a form type is a Section 16 ownership form, ignoring case and surrounding space.
 *
 * @param formType an EDGAR form type, such as `4/A`
 * @returns true for a form in `OWNERSHIP_FORM_TYPES`
 */
export function isOwnershipForm(formType) {
    return OWNERSHIP_FORM_TYPES.includes(formType.trim().toUpperCase());
}
/** Every form Mosaic cares about, for either reason. */
export const SIGNAL_FORM_TYPES = [
    ...DISCOVERY_FORM_TYPES,
    ...MONITORING_FORM_TYPES,
];
/**
 * 8-K item codes that can indicate a separation. 7.01/8.01 usually carry the first
 * announcement; 1.01 the separation agreement; 2.01 completion of the disposition;
 * 5.02 the management changes that accompany one.
 */
export const SIGNAL_8K_ITEMS = ["1.01", "1.02", "2.01", "5.02", "7.01", "8.01"];
/**
 * How far a filing has got through ingestion. A filing that fails partway is retryable
 * without re-walking the stages that already succeeded.
 */
export const IngestState = z.enum(["indexed", "fetched", "extracted", "embedded", "failed"]);
/** One EDGAR submission, with how far Mosaic has got ingesting it. */
export const Filing = z.object({
    accession: Accession,
    cik: Cik,
    companyName: z.string(),
    formType: z.string(),
    filingDate: IsoDate,
    periodOfReport: IsoDate.nullable(),
    acceptanceDatetime: Timestamp.nullable(),
    /** 8-K item codes, e.g. ["7.01","8.01"]. Empty for forms that have no items. */
    items: z.array(z.string()),
    primaryDocument: z.string().nullable(),
    sizeBytes: z.number().int().nonnegative().nullable(),
    isXbrl: z.boolean(),
    ingestState: IngestState,
    failedReason: z.string().nullable(),
    fetchedAt: Timestamp.nullable(),
});
/** One row of an EDGAR daily or quarterly `form.idx`. The rawest useful unit. */
export const IndexEntry = z.object({
    formType: z.string(),
    companyName: z.string(),
    cik: Cik,
    filingDate: IsoDate,
    /** Path as printed in the index, e.g. `edgar/data/320193/0000320193-24-000123.txt`. */
    filePath: z.string(),
    accession: Accession,
});
/** One document within a filing, identified by its accession and sequence. */
export const FilingDocument = z.object({
    id: z.string().uuid(),
    accession: Accession,
    sequence: z.number().int().nonnegative(),
    documentType: z.string().nullable(),
    filename: z.string(),
    description: z.string().nullable(),
    byteSize: z.number().int().nonnegative().nullable(),
    charCount: z.number().int().nonnegative().nullable(),
    hasText: z.boolean(),
});
/**
 * A grid of cells reconstructed from a `<table>` in a filing, with the range of
 * `document_text.body` it occupies.
 *
 * Every cell's text is a verbatim slice of `body`, and the range runs from the first
 * cell to the last, so a reader can render the grid in place of those characters.
 * Joining the cells reproduces the slice only ignoring whitespace: the separator between
 * two cells may be a space or a newline (`documentTables.test.ts`).
 */
export const DocumentTable = z.object({
    /** Ordinal within the document, in reading order. */
    index: z.number().int().nonnegative(),
    /**
     * Absolute UTF-16 offsets into `document_text.body`, half-open — the same scale as
     * every other citation in Mosaic.
     */
    charStart: z.number().int().nonnegative(),
    charEnd: z.number().int().nonnegative(),
    /**
     * `figures` for a table of numbers — a capitalization table, a pro forma, a segment
     * breakout. `prose` otherwise: a definitions table, a signature block, a grid of
     * officers.
     */
    kind: z.enum(["figures", "prose"]),
    /**
     * Cell text, row-major, every row padded to the same width. Columns empty in every row
     * are removed.
     *
     * A `colspan` cell's text sits in the first column it covers and the rest of its span
     * reads empty, which keeps every value under a stable column index across rows.
     * `rowspan` is not interpreted: a cell belongs to the row that declared it.
     */
    rows: z.array(z.array(z.string())),
});
//# sourceMappingURL=filing.js.map